/*
 *   File : add-goal.js
 *   Author : https://evoqins.com
 *   Description : add goal component
 *   Integrations : goal/upsert
 *   Version : 2.0.0
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../../Components/Buttons";
import { Input, CustomFileUpload } from "../../Components/FormElements";
import { APIService } from "../../Service";
import { toast } from "react-toastify";

export default function AddGoal() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);

  // Form states
  const [goalName, setGoalName] = useState("");
  const [goalNameError, setGoalNameError] = useState("");

  const [about, setAbout] = useState("");
  const [aboutError, setAboutError] = useState("");

  const [minSIP, setMinSIP] = useState("");
  const [minSIPErr, setMinSIPErr] = useState("");

  const [minLumpsum, setMinLumpsum] = useState("");
  const [minLumpsumErr, setMinLumpsumErr] = useState("");

  // logo upload
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoError, setLogoError] = useState("");

  useEffect(() => {
    dispatch(setPageTitle(["Manage Goal", "Add"]));
    dispatch(setSearchEnabled(false));
    dispatch(setNavLink(["/manage-goal"]));
  }, []);

  // form validation
  const validateForm = () => {
    let valid = true;

    if (!goalName.trim()) {
      setGoalNameError("Goal name is required");
      valid = false;
    }

    if (!about.trim()) {
      setAboutError("About is required");
      valid = false;
    }

    if (!minSIP.trim()) {
      setMinSIPErr("Min SIP is required");
      valid = false;
    }

    if (!minLumpsum.trim()) {
      setMinLumpsumErr("Min Lumpsum is required");
      valid = false;
    }

    if (!logoFile && !logoUrl) {
      setLogoError("Logo is required");
      valid = false;
    }

    return valid;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setLoader(true);

    const payload = [
      {
        name: goalName.trim(),
        description: about.trim(),
        image: logoUrl,
        min_sip: Number(minSIP),
        min_lumpsum: Number(minLumpsum),
      },
    ];

    APIService(true, "goal/upsert", payload)
      .then((res) => {
        if (res.status_code === 200) {
          toast.success(res.message);
          navigate("/manage-goal");
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
        console.error(error);
      })
      .finally(() => setLoader(false));
  };

  return (
    <div className="e-glass-card bg-transparent rounded-24 p-24 d-flex flex-column row-gap-3">
      <div className="row">
        <div className="col-12">
          <div className="d-flex flex-column e-glass-card bg-opacity-43 shadow-none rounded-16 row-gap-3 p-24">
            <div className="row">
              <div className="col-lg-6 col-12">
                <h4 className="fw-500 fs-18 text-eerie-black">Add Goal</h4>

                <Input
                  id="goal_name"
                  type="text"
                  label="Goal Name"
                  placeholder="Enter Goal Name"
                  className="w-100 px-3 py-10"
                  require={true}
                  value={goalName}
                  error={goalNameError}
                  autoFocus={true}
                  handleChange={(v) => {
                    setGoalName(v);
                    setGoalNameError("");
                  }}
                />

                <Input
                  id="about"
                  type="text"
                  label="About"
                  placeholder="Enter Goal About"
                  require={true}
                  value={about}
                  className="w-100 px-3 py-10"
                  error={aboutError}
                  handleChange={(v) => {
                    setAbout(v);
                    setAboutError("");
                  }}
                />

                <div className="d-flex flex-column row-gap-2 pb-2">
                  <p className="mb-0 fw-400 fs-14">
                    Upload logo <span className="text-cg-red">*</span>
                  </p>
                  <CustomFileUpload
                    file={logoFile || logoUrl}
                    type={2}
                    fileSize={3}
                    handleClear={() => {
                      setLogoFile(null);
                      setLogoUrl("");
                    }}
                    error={logoError}
                    handleChange={(file) => {
                      setLogoFile(file);
                      setLogoError("");
                    }}
                    onUploadComplete={(url) => setLogoUrl(url)}
                  />
                </div>

                <h4 className="fw-500 fs-18 text-eerie-black pt-24">
                  Min SIP & Lumpsum
                </h4>

                <div className="position-relative">
                  <Input
                    id="min_sip"
                    type="Number"
                    label="Min SIP"
                    placeholder="Enter Min SIP"
                    require={true}
                    value={minSIP}
                    error={minSIPErr}
                    className="w-100 ps-4 pe-3 py-10"
                    handleChange={(v) => {
                      setMinSIP(v);
                      setMinSIPErr("");
                    }}
                  />
                  <span
                    style={{ top: "41%", left: "2%" }}
                    className="position-absolute"
                  >
                    ₹
                  </span>
                </div>

                <div className="position-relative">
                  <Input
                    id="min_lumpsum"
                    type="Number"
                    label="Min Lumpsum"
                    placeholder="Enter Min Lumpsum"
                    require={true}
                    className="w-100 ps-4 pe-3 py-10"
                    value={minLumpsum}
                    error={minLumpsumErr}
                    handleChange={(v) => {
                      setMinLumpsum(v);
                      setMinLumpsumErr("");
                    }}
                  />
                  <span
                    style={{ top: "41%", left: "2%" }}
                    className="position-absolute"
                  >
                    ₹
                  </span>
                </div>
              </div>

              <div className="col-12">
                <div className="row">
                  <div className="col-lg-6 offset-lg-2 col-12">
                    <div className="row row-cols-2 pt-24">
                      <div className="col">
                        <PrimaryButton
                          name="Cancel"
                          ClassName="rounded-pill bg-transparent border-oxford-blue py-3 text-oxford-blue w-100 fs-16 fw-500"
                          onPress={() => navigate("/manage-goal")}
                        />
                      </div>
                      <div className="col">
                        <PrimaryButton
                          loading={loader}
                          name="Create"
                          ClassName="rounded-pill bg-oxford-blue py-3 text-white w-100 fs-16 fw-500"
                          onPress={handleSubmit}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}